import axios from 'axios';

import { CLIENT_HEADER_VALUE } from '@/config/client';
import { getActiveLocale } from '@/i18n';
import { CUSTOM_EVENT_SESSION_LOGOUT } from '@/state/session';
import { CLIENT_HEADER_NAME } from '@/utils/clientHeader';
import { normalizeApiError } from '@/utils/error';

/**
 * Without `VITE_API_BASE_URL` axios calls the Vite dev server itself: `GET /me` comes back as the
 * SPA's index.html with a 200 (so the portal looks signed in and lands on onboarding) and every
 * POST is a 404, sign-out included. Fail at boot instead, pointing to `.env.example`.
 */
if (import.meta.env.MODE === 'development' && !import.meta.env.VITE_API_BASE_URL) {
  throw new Error(
    'VITE_API_BASE_URL is not set: copy .env.example to .env (or run `VITE_API_BASE_URL=http://localhost:3000 npm run dev`)',
  );
}

/**
 * Same instance as bodyprint-web, minus the workspace and WebView headers: the partner portal has
 * no workspace (the API resolves the partner from the session) and never runs inside the app.
 */
export const apiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  // Names the client and version in the API logs (`x-client`, see `src/config/client.ts`).
  headers: { [CLIENT_HEADER_NAME]: CLIENT_HEADER_VALUE },
});

const isSigninRoute = (url?: string) => {
  if (!url) return false;
  const normalizedUrl = url.replace(/^\//, '').split('?')[0];
  return normalizedUrl === 'auth/signin';
};

apiInstance.interceptors.request.use(
  (config) => {
    config.headers['Accept-Language'] = getActiveLocale();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.response?.config?.url;

    if (status === 401 && !isSigninRoute(url)) {
      document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_SESSION_LOGOUT));
    }

    return Promise.reject(normalizeApiError(error));
  },
);
